import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import {
    EMPTY_ACTIVITY_FEED_FILTERS,
    activityFeedFiltersToApiParams,
    type ActivityFeedApiFilterParams,
    type ActivityFeedFiltersValue,
} from '../components/activityFeedFilters';

export type ActivityLogEventType =
    | 'order-created'
    | 'part-excluded'
    | 'part-included'
    | 'part-marked-ordered'
    | 'part-unmarked-ordered'
    | 'part-excluded-from-quoting'
    | 'part-quoting-restored'
    | 'quotes-complete'
    | 'part-selections-changed'
    | 'orders-complete'
    | 'order-completed'
    | 'order-uncompleted'
    | 'order-archived'
    | 'order-unarchived'
    | 'order-tag-changed'
    | 'supplier-rejected'
    | 'supplier-cancelled-parts'
    | 'supplier-returned-parts'
    | 'shop-cancel-or-return'
    | 'supplier-order-confirmed'
    | 'quote-request-submitted'
    | 'order-placement-requested'
    | 'quoting-skipped'
    | 'order-details-edited'
    | 'flag-removed'
    | 'list-price-adjusted'
    | 'option-note-changed'
    | 'reset-to-partbay-selections'
    | 'parts-received'
    | 'parts-unreceived'
    | 'workflow-cancelled'
    | 'auto-order'
    | 'request-quotes'
    | 'place-order'
    | (string & {});

export interface EnrichedActivityLogEntry {
    _id: string;
    orderId: string;
    userId: string | null;
    userName: { firstName: string; lastName: string; } | null;
    timestamp: string;
    eventType: ActivityLogEventType;
    metadata?: Record<string, string>;
    /** Non-human origin of the entry (e.g. 'carl-agent'). Absent/null for direct human actions. */
    source?: 'carl-agent' | 'nexsyis' | null;
}

export interface EnrichedActivityLogResponse {
    logs: EnrichedActivityLogEntry[];
    totalCount: number;
}

export interface FetchOrderActivityLogsInput extends ActivityFeedApiFilterParams {
    orderId: string;
    page?: number;
    limit?: number;
    search?: string;
}

const getActivityLogs = async (
    sessionId: string,
    input: FetchOrderActivityLogsInput,
): Promise<EnrichedActivityLogResponse> => {
    const { data } = await axios.post(`${API_URL}/order/activity-logs`,
        input,
        { headers: { Authorization: sessionId } },
    );
    return data;
};

export const useGetActivityLogs = (
    orderId: string,
    page = 1,
    limit = 50,
    enabled = true,
    search = '',
    filters: ActivityFeedFiltersValue = EMPTY_ACTIVITY_FEED_FILTERS,
    mapEventTypes?: (selected: string[]) => string[],
) => {
    const sessionContext = useContext(SessionContext);
    const trimmedSearch = search.trim();

    return useQuery({
        // Key on dateFilter enum (inside filters), not absolute since — queryFn recomputes since.
        queryKey: ['/order/activity-logs', orderId, page, limit, trimmedSearch, filters],
        queryFn: () => getActivityLogs(sessionContext?.sessionId ?? '', {
            orderId,
            page,
            limit,
            ...(trimmedSearch ? { search: trimmedSearch } : {}),
            ...activityFeedFiltersToApiParams(filters, mapEventTypes),
        }),
        enabled: !!sessionContext?.sessionId && !!orderId && enabled,
        staleTime: 30 * 1000,
        refetchInterval: 30 * 1000,
        refetchOnWindowFocus: false,
        placeholderData: (prev, prevQuery) =>
            prevQuery?.queryKey[1] === orderId ? prev : undefined,
    });
};
